import { useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";

const REPORTS = [
  { key: "team-sales",        label: "Team sales",         endpoint: "/sales/team",       icon: FileSpreadsheet },
  { key: "leads",             label: "Leads",              endpoint: "/sales/leads",      icon: FileText },
  { key: "quotations",        label: "Quotations",         endpoint: "/sales/quotations", icon: FileText },
  { key: "orders",            label: "Orders",             endpoint: "/sales/orders",     icon: FileSpreadsheet },
  { key: "customers",         label: "Customer master",    endpoint: "/sales/customers",  icon: FileText },
  { key: "dealers",           label: "Dealer performance", endpoint: "/sales/dealers",    icon: FileText },
  { key: "visits",            label: "Visit report",       endpoint: "/sales/visits",     icon: FileText },
  { key: "outstanding",       label: "Outstanding (AR)",   endpoint: "/sales/collections", icon: FileSpreadsheet },
];

function toCSV(rows) {
  if (!rows.length) return "";
  const keys = Array.from(new Set(rows.flatMap((r) => Object.keys(r))));
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [keys.join(","), ...rows.map((r) => keys.map((k) => esc(r[k])).join(","))].join("\n");
}

export default function Reports() {
  const [loading, setLoading] = useState(null);
  const download = async (r, format) => {
    setLoading(r.key + format);
    try {
      const { data } = await api.get(r.endpoint);
      const rows = Array.isArray(data) ? data : [];
      const blob = format === "csv"
        ? new Blob([toCSV(rows)], { type: "text/csv" })
        : new Blob([JSON.stringify(rows, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${r.key}.${format}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${r.label} exported`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(null); }
  };
  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="sales-reports">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black">Reports</h1>
        <p className="text-muted-foreground">Personal & team sales reports. CSV/JSON ready. PDF/Excel rendering: next iteration.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {REPORTS.map((r) => (
          <Card key={r.key} data-testid={`sreport-${r.key}`}>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center gap-2"><r.icon className="w-5 h-5 text-primary" /><div className="font-semibold">{r.label}</div></div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => download(r, "csv")} disabled={loading === r.key + "csv"} data-testid={`sreport-${r.key}-csv`}><Download className="w-4 h-4 mr-1" /> CSV</Button>
                <Button size="sm" variant="outline" onClick={() => download(r, "json")} disabled={loading === r.key + "json"} data-testid={`sreport-${r.key}-json`}><Download className="w-4 h-4 mr-1" /> JSON</Button>
                <Badge variant="outline" className="text-[10px]">PDF · soon</Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
